/**
 * What the status screen shows, decided without a DOM.
 *
 * The same split the old panel used for `web/*-view.ts`: the judgements live in
 * a pure module and the drawing lives beside them. Which buttons a campaign
 * offers, and which number to believe about what it cost, are exactly the
 * things worth a test — and exactly the things that are untestable once they
 * are three lines inside a render function.
 */

export interface CampaignFigures {
  readonly status: string;
  readonly recipient_count: number;
  readonly accepted_count: number;
  readonly failed_count: number;
  readonly estimated_parts: number;
  readonly actual_parts: number | null;
}

export interface Available {
  /** Stop, while it is running. */
  readonly stop: boolean;
  /** Resume, while something is left to send. */
  readonly resume: boolean;
  /** Retry, whenever anything failed and nothing is running. */
  readonly retry: boolean;
  /** How many have not been settled either way. */
  readonly pending: number;
}

/**
 * Which of the three controls to offer.
 *
 * Retry is offered on a *finished* campaign as well: "some of them failed" is
 * exactly when somebody wants to try those again, and hiding the button until
 * the campaign is somehow un-finished would be the panel deciding that for them.
 *
 * A draft offers none of them. It has not started, so there is nothing to stop,
 * resume or retry — the only thing to do with a draft is confirm it.
 */
export function available(campaign: CampaignFigures): Available {
  const running = campaign.status === 'sending';
  const pending = Math.max(
    0,
    campaign.recipient_count - campaign.accepted_count - campaign.failed_count,
  );

  if (campaign.status === 'draft') return { stop: false, resume: false, retry: false, pending };

  return {
    stop: running,
    resume: (campaign.status === 'paused' || campaign.status === 'cancelled') && pending > 0,
    retry: !running && campaign.failed_count > 0,
    pending,
  };
}

export interface PartsShown {
  readonly parts: number;
  /** Whether the provider counted something different from the panel's estimate. */
  readonly disagrees: boolean;
  readonly estimate: number;
}

/**
 * How many SMS this campaign is, and whose number that is.
 *
 * **The provider's count wins**, because it is what the bill is calculated from
 * — where the two differ, ours is wrong. The disagreement is reported rather
 * than hidden: a panel that quietly showed its own number would be a panel that
 * under-reports what a campaign cost.
 *
 * Zero and null both mean "the provider has not said yet", not "it is free".
 */
export function partsShown(campaign: CampaignFigures): PartsShown {
  const actual = campaign.actual_parts;
  const known = actual !== null && actual > 0;

  return {
    parts: known ? actual : campaign.estimated_parts,
    disagrees: known && actual !== campaign.estimated_parts,
    estimate: campaign.estimated_parts,
  };
}

export interface Paging {
  readonly needed: boolean;
  readonly page: number;
  readonly pages: number;
  readonly from: number;
  readonly to: number;
  readonly previous?: number;
  readonly next?: number;
}

/**
 * Where in ten thousand rows this page is.
 *
 * Positions are one-based because they are read by a person, and the last page
 * is not assumed to be full — `to` comes from what actually arrived rather than
 * from the arithmetic, so a filtered list does not claim rows it did not show.
 */
export function paging(offset: number, shown: number, total: number, size: number): Paging {
  const pages = Math.max(1, Math.ceil(total / size));
  const page = Math.floor(offset / size) + 1;
  const hasPrevious = offset > 0;
  const hasNext = offset + shown < total;

  return {
    needed: total > size,
    page,
    pages,
    from: total === 0 ? 0 : offset + 1,
    to: offset + shown,
    ...(hasPrevious ? { previous: Math.max(0, offset - size) } : {}),
    ...(hasNext ? { next: offset + size } : {}),
  };
}
